import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { setSignatory, setBrandOverride } from "@/lib/pdf";
import { setDateFormat } from "@/lib/dateFormat";
import { resetTeamLogos } from "@/lib/teamLogos";
import { resetClubLogos } from "@/lib/clubLogoStore";

const AuthContext = createContext(null);

// #RRGGBB -> "H S% L%" for the Tailwind --primary CSS variable.
function hexToHslVar(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Perceptual luminance (0..1) to decide dark/light foreground text over a brand color.
function relLuminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

const BRAND_VARS = ["--primary", "--primary-foreground", "--ring", "--accent-foreground", "--club-2", "--club-2-foreground"];
const BG_VARS = ["--background", "--bg-ov1", "--bg-ov2", "--bg-ov-aside1", "--bg-ov-aside2"];
const WINDOW_VARS = ["--card", "--popover", "--muted", "--secondary", "--border"];
const TEXT_VARS = ["--foreground", "--card-foreground", "--popover-foreground", "--secondary-foreground", "--muted-foreground"];

// #RRGGBB -> { h, s, l } with s,l in 0..100.
function hexToHslParts(hex) {
  const v = hexToHslVar(hex);
  if (!v) return null;
  const [h, s, l] = v.replace(/%/g, "").split(" ").map(Number);
  return { h, s, l };
}
function hslStr(p, dL = 0) { return `${p.h} ${p.s}% ${Math.max(0, Math.min(100, Math.round(p.l + dL)))}%`; }

function applyBranding(org) {
  setDateFormat(org && org.date_format ? org.date_format : "eu");
  const root = document.documentElement;
  BRAND_VARS.forEach((v) => root.style.removeProperty(v));
  BG_VARS.forEach((v) => root.style.removeProperty(v));
  WINDOW_VARS.forEach((v) => root.style.removeProperty(v));
  TEXT_VARS.forEach((v) => root.style.removeProperty(v));
  const gender = org && org.squad_gender === "male" ? "male" : "female";
  try { localStorage.setItem("pc_gender", gender); } catch (e) { /* ignore */ }
  window.dispatchEvent(new CustomEvent("pc-gender-change", { detail: gender }));
  const primary = org && org.primary_color;
  const primHsl = primary ? hexToHslVar(primary) : null;
  if (primHsl) {
    const fg = relLuminance(primary) > 0.55 ? "216 75% 8%" : "0 0% 98%";
    root.style.setProperty("--primary", primHsl);
    root.style.setProperty("--primary-foreground", fg);
    root.style.setProperty("--ring", primHsl);
    root.style.setProperty("--accent-foreground", primHsl);
  }
  const secondary = org && org.secondary_color;
  const secHsl = secondary ? hexToHslVar(secondary) : null;
  if (secHsl) {
    root.style.setProperty("--club-2", secHsl);
    root.style.setProperty("--club-2-foreground", relLuminance(secondary) > 0.55 ? "216 75% 8%" : "0 0% 98%");
  }
  // Custom app surface ("window color") — tints the whole app surface, overlays AND panels/cards.
  const base = org && org.base_color;
  const baseParts = base ? hexToHslParts(base) : null;
  if (baseParts) {
    const m = /^#?([0-9a-f]{6})$/i.exec(base.trim());
    const n = m ? parseInt(m[1], 16) : 0;
    const rgb = `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
    root.style.setProperty("--background", hslStr(baseParts, -4));
    root.style.setProperty("--bg-ov1", `rgba(${rgb}, 0.965)`);
    root.style.setProperty("--bg-ov2", `rgba(${rgb}, 0.985)`);
    root.style.setProperty("--bg-ov-aside1", `rgba(${rgb}, 0.90)`);
    root.style.setProperty("--bg-ov-aside2", `rgba(${rgb}, 0.95)`);
    // Panels / "windows": anchored at the chosen colour, with derived shades above it.
    root.style.setProperty("--card", hslStr(baseParts, 0));
    root.style.setProperty("--popover", hslStr(baseParts, 0));
    root.style.setProperty("--muted", hslStr(baseParts, 3));
    root.style.setProperty("--secondary", hslStr(baseParts, 8));
    root.style.setProperty("--border", hslStr(baseParts, 10));
  }
  // Background texture: swap the built-in /assets/texture_dark.jpg for the club's own
  // uploaded texture (if any) and let it show through at the chosen opacity.
  const tex = org && org.texture_url;
  if (tex) {
    const turl = tex.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${tex}` : tex;
    root.style.setProperty("--app-texture-url", `url("${turl}")`);
    root.style.setProperty("--app-texture-size", org.texture_mode === "cover" ? "cover" : "480px");
    root.style.setProperty("--app-texture-aside-size", org.texture_mode === "cover" ? "cover" : "300px");
    const op = Number(org.texture_opacity) === 12 ? 0.12 : 0.06;
    const bm = base && /^#?([0-9a-f]{6})$/i.exec(String(base).trim());
    const n = bm ? parseInt(bm[1], 16) : 0x0b1a33;
    const rgb = `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
    root.style.setProperty("--bg-ov1", `rgba(${rgb}, ${1 - op})`);
    root.style.setProperty("--bg-ov2", `rgba(${rgb}, ${1 - op * 0.5})`);
    root.style.setProperty("--bg-ov-aside1", `rgba(${rgb}, ${1 - op})`);
    root.style.setProperty("--bg-ov-aside2", `rgba(${rgb}, ${1 - op * 0.5})`);
  } else {
    root.style.removeProperty("--app-texture-url");
    root.style.removeProperty("--app-texture-size");
    root.style.removeProperty("--app-texture-aside-size");
  }
  // Custom text colour — default white; applies to all primary/body text surfaces.
  const text = org && org.text_color;
  const textParts = text ? hexToHslParts(text) : null;
  if (textParts) {
    const tv = hslStr(textParts, 0);
    ["--foreground", "--card-foreground", "--popover-foreground", "--secondary-foreground"].forEach((v) => root.style.setProperty(v, tv));
    // Secondary/muted text = lower-contrast variant of the chosen colour.
    root.style.setProperty("--muted-foreground", hslStr(textParts, textParts.l > 50 ? -32 : 32));
  }
  // PDF/JPEG exports follow the club's own crest + name (set in Settings) for EVERY tenant,
  // including the founding club once it uploads a crest or renames itself. When no custom crest
  // is set, crestUrl stays "" and the export falls back to the built-in default assets.
  if (org) {
    const crestUrl = org.crest_url ? (org.crest_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${org.crest_url}` : org.crest_url) : "";
    setBrandOverride({ crestUrl, primaryColor: org.primary_color || "", secondaryColor: org.secondary_color || "", clubName: org.name || "" });
  } else {
    setBrandOverride(null);
  }
  // Favicon + tab title ALWAYS follow the club's own logo/name — including the founding club
  // once it has set its own name/crest in Settings. Changes automatically when they update it.
  const favUrl = org && org.crest_url
    ? (org.crest_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${org.crest_url}` : org.crest_url)
    : (org && org.is_default ? "/assets/parma_crest.png" : "/favicon.png");
  setFavicon(favUrl);
  const clubNm = org && org.name && String(org.name).trim() ? org.name : (org && org.is_default ? "Parma Calcio 1913 Women" : null);
  document.title = clubNm ? `${clubNm} · Technical Hub` : "Technical Hub";
}

function setFavicon(url) {
  document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']").forEach((l) => { l.href = url; });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged

  useEffect(() => {
    api.get("/auth/me").then((res) => setUser(res.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    setSignatory(user && typeof user === "object" ? user : null);
    applyBranding(user && typeof user === "object" ? user.org : null);
  }, [user]);

  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const login = async (email, password) => {
    resetTeamLogos();
    resetClubLogos();
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("pc_token", data.token);
    await refreshMe();
    return data;
  };

  const register = async ({ clubName, email, password, name, plan }) => {
    const lang = (() => { try { return localStorage.getItem("pc_lang") === "en" ? "en" : "it"; } catch (e) { return "it"; } })();
    const { data } = await api.post("/auth/register", { club_name: clubName, email, password, name, plan: plan || "", lang });
    if (data.token) {
      localStorage.setItem("pc_token", data.token);
      await refreshMe();
    }
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("pc_token");
    resetTeamLogos();
    resetClubLogos();
    applyBranding(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
